// Local form state + cloud auto-save for the risk-assessment wizard.
//
// Behaviour:
//   * Holds the four answer dicts (rtq / behavioral / fc / style) in a
//     single `answers` object so the wizard can spread them straight
//     into the /calculate-full-profile payload.
//   * Debounces saves: 5s after the most recent edit, the current
//     `answers` JSON is shipped to POST /assessment/save-progress and
//     a deep-equality fingerprint (`lastSaved`) is updated so a second
//     save is only fired when the user actually changes something.
//   * Exposes a save *status* so the wizard can render a "Saved" /
//     "Saving…" / "Save failed" affordance next to the progress bar.
//   * Hydrates from a server-side draft when the caller passes one
//     in via `initialAnswers` -- typically the `raw_answers` column
//     from GET /assessment/results, giving the user a "resume where
//     you left off" experience even after closing the browser.
//
// The hook is deliberately UI-agnostic: it returns plain state +
// setters, NOT pre-bound input handlers, so the wizard can choose how
// to render each question shape.

import { useCallback, useEffect, useRef, useState } from 'react';

import { riskApi, type RawAnswers } from '../api/riskApi';

/** Empty draft -- every sub-dict starts as `{}` so the user can
 *  partially fill any section in any order. */
export const EMPTY_ANSWERS: RawAnswers = {
    rtq_answers: {},
    behavioral_answers: {},
    fc_answers: {},
    style_answers: {},
};

/** Auto-save heartbeat in milliseconds. 5s matches the spec ("every
 *  5 questions" -- in practice each click resets the timer). */
const AUTOSAVE_DEBOUNCE_MS = 5_000;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseAssessmentFormResult {
    answers: RawAnswers;
    setAnswers: React.Dispatch<React.SetStateAction<RawAnswers>>;
    /**
     * Imperative setter for a single question's answer. Pure
     * convenience over `setAnswers` so callers don't have to spread
     * the right sub-dict by hand on every click.
     */
    setAnswer: (
        section: keyof RawAnswers,
        questionId: string,
        value: string | number,
    ) => void;
    saveStatus: SaveStatus;
    /** Force-flush the debounced save (called on Calculate / unmount). */
    flushSave: () => Promise<void>;
}

/**
 * @param initialAnswers Optional draft to hydrate from (typically the
 *                       `raw_answers` column of GET /results). When
 *                       omitted, the form starts from EMPTY_ANSWERS.
 * @param enabled        When false, the hook still returns state but
 *                       does NOT auto-save. The wizard sets this to
 *                       false during the loading phase so an empty
 *                       draft never overwrites a real persisted one.
 */
export function useAssessmentForm(
    initialAnswers?: RawAnswers | null,
    enabled = true,
): UseAssessmentFormResult {
    const [answers, setAnswers] = useState<RawAnswers>(
        initialAnswers ?? EMPTY_ANSWERS,
    );
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

    // Fingerprint of the most recently-saved payload. We compare
    // JSON.stringify() output rather than reference equality because
    // setAnswer always returns a fresh object even when the value
    // doesn't change (e.g. the user re-clicks the same option).
    const lastSavedRef = useRef<string>(JSON.stringify(answers));

    // Imperative escape hatch so the wizard can flush before unmount /
    // before /calculate-full-profile fires. Held in a ref so the
    // closure inside the debounce timer can call the latest version.
    const flushSaveRef = useRef<() => Promise<void>>(async () => {});

    flushSaveRef.current = async () => {
        const current = JSON.stringify(answers);
        if (current === lastSavedRef.current) return;
        try {
            setSaveStatus('saving');
            await riskApi.saveProgress(answers);
            lastSavedRef.current = current;
            setSaveStatus('saved');
        } catch (err) {
            // Auto-save is fire-and-forget UX: surface the failure
            // through `saveStatus` so the wizard can hint at it, but
            // never block the user from continuing. The /save-results
            // call at the end of the wizard is the authoritative
            // persistence step -- a transient /save-progress failure
            // is recoverable on the next edit.
            // eslint-disable-next-line no-console
            console.warn('[useAssessmentForm] auto-save failed', err);
            setSaveStatus('error');
        }
    };

    // Hydrate when the server-side draft arrives AFTER mount (the
    // wizard kicks off `getLatestResults()` in parallel with mount).
    // We replace state in one go and update the fingerprint so the
    // hydration itself does NOT trigger a redundant /save-progress.
    useEffect(() => {
        if (!initialAnswers) return;
        setAnswers(initialAnswers);
        lastSavedRef.current = JSON.stringify(initialAnswers);
        // We only want to react to `initialAnswers` changing identity,
        // which is how the wizard signals "draft just arrived".
         
    }, [initialAnswers]);

    // Debounced auto-save. Every change to `answers` resets a 5s timer;
    // when the timer fires, we ship the current state to the backend.
    useEffect(() => {
        if (!enabled) return;
        const handle = setTimeout(() => {
            void flushSaveRef.current();
        }, AUTOSAVE_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [answers, enabled]);

    // Best-effort flush on unmount so a quick "answer last question →
    // navigate away" doesn't lose the final edit. Errors are swallowed
    // here; the user has already left the page.
    useEffect(() => {
        return () => {
            void flushSaveRef.current().catch(() => undefined);
        };
    }, []);

    const setAnswer = useCallback(
        (section: keyof RawAnswers, questionId: string, value: string | number) => {
            setAnswers((prev) => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [questionId]: value,
                },
            }));
            // Optimistic UI: when the user starts editing again, drop
            // the "saved" badge so the wizard can show "Saving…" once
            // the debounce fires. If a save is currently in flight
            // we leave the status alone so the spinner is uninterrupted.
            setSaveStatus((s) => (s === 'saving' ? s : 'idle'));
        },
        [],
    );

    const flushSave = useCallback(() => flushSaveRef.current(), []);

    return { answers, setAnswers, setAnswer, saveStatus, flushSave };
}
